/**
 * BondDocumentUpload - Upload documents directly to a large bond (no power required)
 * Used for Pending status bonds where powers haven't been added yet.
 */

import { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { OathButton, OathAlert } from '../../../shared/OathUI';
import { uploadPowerProof } from '../../../../services/oathLargeBondService';

const BondDocumentUpload = ({ bondId }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleUpload = async () => {
    if (!files.length || !bondId) return;
    setUploading(true);
    setResult(null);

    const response = await uploadPowerProof(bondId, files);
    setResult({
      success: response.success,
      message: response.success
        ? response.message || `${files.length} file(s) uploaded successfully`
        : response.error || 'Upload failed',
    });

    if (response.success) {
      setFiles([]);
      toast.success(`${files.length} file(s) uploaded`);
    }
    setUploading(false);
  };

  return (
    <div>
      <h4 className="font-medium text-gray-900 dark:text-white mb-2">Documents</h4>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
        Upload supporting documents for this bond request.
      </p>
      <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-4">
        <div className="flex flex-col sm:flex-row items-start gap-2">
          <OathButton
            variant="outline"
            size="sm"
            icon={ArrowUpTrayIcon}
            onClick={() => fileInputRef.current?.click()}
          >
            Select Files
          </OathButton>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            multiple
            className="hidden"
            onChange={(e) => {
              setFiles(Array.from(e.target.files));
              setResult(null);
            }}
          />
          {files.length > 0 && (
            <OathButton size="sm" onClick={handleUpload} loading={uploading}>
              Upload {files.length} File{files.length > 1 ? 's' : ''}
            </OathButton>
          )}
        </div>
        {files.length > 0 && (
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            {files.map((f, i) => <p key={i}>{f.name}</p>)}
          </div>
        )}
        {result && (
          <OathAlert
            variant={result.success ? 'success' : 'error'}
            message={result.message}
            className="mt-2"
          />
        )}
      </div>
    </div>
  );
};

export default BondDocumentUpload;
